import React, { useState, useEffect, useRef } from "react";
import { useCategoryAnalysis } from "../CategoryAnalysis/context/CategoryAnalysisContext";
import FilterPanel from "../KeywordTrackingNewUIPage/FilterPanel";
import AppsTrackingPlanModal from '../Common/Modal/AppsTrackingPlanModal';
import { userProfileApi } from '../../services/userProfileApi';
import { useFilters } from '../../hooks/useFilters';
import axios from 'axios';
import { api } from '../../utils/variables';
import { usePlatforms } from '../../contexts/PlatformsContext';
import { useAnalytics, ANALYTICS_EVENTS } from "../../analytics";

const API_BASE_URL = api;

const Header = () => {
  const { trackEvent } = useAnalytics();
  const { getActivePlatforms, loading: platformsLoading } = usePlatforms();
  const {
    setActiveKeyword,
    activeKeyword,
    applyCategoryFilters,
    platform,
  } = useCategoryAnalysis();
  const { filterSections } = useFilters();
  const [searchInput, setSearchInput] = useState("");
  const [tableDataLoading, setTableDataLoading] = useState(false);
  const filterButtonRef = useRef(null);

  const [suggestions, setSuggestions] = useState([]);
  const [isSearchingSuggestions, setIsSearchingSuggestions] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const debounceTimerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchSuggestions = async (query) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      setIsSearchingSuggestions(false);
      return;
    }

    setIsSearchingSuggestions(true);
    try {
      console.log(`Searching genres for: ${query} using ${API_BASE_URL}`);
      const res = await axios.get(`${API_BASE_URL}/tracker/genres/search`, {
        params: { q: query, platform: platform || '' },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`
        }
      });
      console.log("Suggestions response:", res.data);
      if (res.data?.results) {
        setSuggestions(res.data.results);
        setShowDropdown(true);
      } else {
        setSuggestions([]);
        setShowDropdown(true);
      }
    } catch (err) {
      console.error("Genre search failed:", err);
      setSuggestions([]);
      setShowDropdown(false);
    } finally {
      setIsSearchingSuggestions(false);
    }
  };

  const handleInputChange = (val) => {
    setSearchInput(val);

    if (val.trim() === '') {
      setIsSearchingSuggestions(false);
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    setIsSearchingSuggestions(true);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      fetchSuggestions(val);
    }, 400);
  };

  const handleSuggestionClick = (genre) => {
    setSearchInput(genre);
    setShowDropdown(false);
    setActiveKeyword(genre);
    trackEvent(ANALYTICS_EVENTS.FEATURE.CATEGORY_SEARCH, { genre, platform });
    setTableDataLoading(true);
    setTimeout(() => {
      setTableDataLoading(false);
    }, 1000);
    try { sessionStorage.setItem('categoryAnalysisLastSearch', genre); } catch (e) { /* ignore */ }
  };

  const clearPersistedSearch = () => {
    try {
      sessionStorage.removeItem('categoryAnalysisLastSearch');
      sessionStorage.removeItem('categoryAnalysisLastResponse');
    } catch (e) {
      // ignore storage errors
    }
  };

  const handleClearSearch = () => {
    trackEvent(ANALYTICS_EVENTS.FEATURE.CATEGORY_SEARCH_CLEARED, { genre: activeKeyword });
    setSearchInput('');
    try { setActiveKeyword(''); } catch (e) { /* noop */ }
    // also clear any persisted search so route changes or reloads don't resurrect it
    clearPersistedSearch();
  };

  const handleSearch = () => {
    const term = searchInput.trim();
    if (!term) {
      try { setActiveKeyword(''); } catch (e) { /* ignore */ }
      clearPersistedSearch();
      return;
    }

    // Restriction: Only allow searching if the term exists in the suggestions list (case-insensitive)
    const isValidSuggestion = suggestions.some(s => s.toLowerCase() === term.toLowerCase());

    if (!isValidSuggestion) {
      console.warn("Search restricted: term not in suggestions", term);
      // Optional: show a brief hint or just prevent search
      return;
    }

    // Close suggestions and cancel pending fetch since we're triggering a primary search for a valid term
    setShowDropdown(false);
    setIsSearchingSuggestions(false);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Use the exact suggestion case if possible
    const matchedSuggestion = suggestions.find(s => s.toLowerCase() === term.toLowerCase()) || term;

    if (matchedSuggestion === activeKeyword) {
      setActiveKeyword('');
      setTimeout(() => setActiveKeyword(matchedSuggestion), 50);
    } else {
      setActiveKeyword(matchedSuggestion);
    }
    trackEvent(ANALYTICS_EVENTS.FEATURE.CATEGORY_SEARCH, { genre: matchedSuggestion, platform });

    // Trigger spinner and reload results
    setTableDataLoading(true);
    // Simulate API call cleanup
    setTimeout(() => {
      setTableDataLoading(false);
    }, 1000);
  };
  const [showFilters, setShowFilters] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isExplorerUser, setIsExplorerUser] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState(() => {
    try {
      const raw = sessionStorage.getItem('categoryAnalysisActiveFilters');
      if (raw) return JSON.parse(raw);
    } catch (e) {
      // ignore
    }
    return {};
  });

  const convertFiltersToApi = (sel) => {
    const apiFilters = {};
    if (sel) {
      if (sel.price) apiFilters.price = sel.price;
      if (sel.rating) apiFilters.rating = sel.rating;
      if (sel.downloads) apiFilters.downloads = sel.downloads;
      if (sel.revenue) apiFilters.revenue = sel.revenue;
      if (sel.release_date) apiFilters.release_date = sel.release_date;
      if (sel.genre) {
        apiFilters.genre = Array.isArray(sel.genre)
          ? sel.genre.map((g) => String(g).toLowerCase())
          : String(sel.genre).toLowerCase();
      }
      if (sel.category) {
        apiFilters.category = Array.isArray(sel.category)
          ? sel.category.map((c) => String(c).toLowerCase())
          : String(sel.category).toLowerCase();
      }
      if (sel.bundle) apiFilters.bundle = sel.bundle;
      if (sel.number_of_players) apiFilters.number_of_players = sel.number_of_players;
      if (sel.platform) apiFilters.platform = sel.platform;
      if (sel.sort_by) apiFilters.sort_by = sel.sort_by;
      else if (sel.sort) apiFilters.sort_by = sel.sort;
    }
    return apiFilters;
  };

  // Restore persisted filters and last search on mount
  useEffect(() => {
    try {
      const rawFilters = sessionStorage.getItem('categoryAnalysisActiveFilters');
      const savedFilters = rawFilters ? JSON.parse(rawFilters) : null;
      if (savedFilters) {
        setAppliedFilters(savedFilters);
        applyCategoryFilters(convertFiltersToApi(savedFilters));
      }
    } catch (e) {
      // ignore
    }

    try {
      const rawKw = sessionStorage.getItem('categoryAnalysisLastSearch');
      const kw = rawKw ? String(rawKw) : null;
      if (kw) {
        setSearchInput(kw);
        try { setActiveKeyword(kw); } catch (e) { /* ignore */ }
      }
    } catch (e) {
      // ignore
    }
  }, []);

  // Keep the search input in sync with the currently active keyword (e.g., when a Top Genre tag is clicked)
  useEffect(() => {
    // If activeKeyword changes (including being set by KeywordTags), mirror it into the input box
    setSearchInput(activeKeyword || '');
  }, [activeKeyword]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await userProfileApi.fetchCurrentPlan();
        const hasProduct = !!(res && res.data && res.data.product);
        if (mounted) setIsExplorerUser(!hasProduct);
      } catch (err) {
        // on error assume explorer (safe default to lock)
        if (mounted) setIsExplorerUser(true);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const handleApplyFilters = (sel) => {
    setAppliedFilters(sel || {});
    try {
      sessionStorage.setItem('categoryAnalysisActiveFilters', JSON.stringify(sel || {}));
    } catch (e) {
      // ignore
    }

    applyCategoryFilters(convertFiltersToApi(sel));
    setShowFilters(false);

    try {
      const nonEmpty = Object.entries(sel || {}).reduce((acc, [k, v]) => {
        if (v == null) return acc;
        if (Array.isArray(v) && v.length === 0) return acc;
        if (typeof v === 'string' && v.trim() === '') return acc;
        acc[k] = v;
        return acc;
      }, {});
      trackEvent(ANALYTICS_EVENTS.FEATURE.CATEGORY_EXPLORER_FILTER_APPLIED, {
        genre: activeKeyword,
        filters: JSON.stringify(nonEmpty).slice(0, 100),
      });
    } catch (e) {
      // ignore analytics errors
    }
  };

  const handleRemoveFilter = (key) => {
    const newFilters = { ...(appliedFilters || {}) };
    if (!Object.prototype.hasOwnProperty.call(newFilters, key)) return;

    delete newFilters[key];
    setAppliedFilters(newFilters);
    try {
      sessionStorage.setItem('categoryAnalysisActiveFilters', JSON.stringify(newFilters || {}));
    } catch (e) {
      // ignore
    }

    applyCategoryFilters(convertFiltersToApi(newFilters));
    trackEvent(ANALYTICS_EVENTS.FEATURE.CATEGORY_EXPLORER_FILTER_REMOVED, { genre: activeKeyword, filter_key: key });
  };

  // Persist last search term when user triggers search
  useEffect(() => {
    try {
      if (activeKeyword && String(activeKeyword).trim()) {
        try { sessionStorage.setItem('categoryAnalysisLastSearch', String(activeKeyword).trim()) } catch (e) { /* ignore */ }
      }
    } catch (e) { }
  }, [activeKeyword])

  return (
    <header className="ca-header">
      <div className="ca-header__content">
        <div className="ca-header__titles">
          <h1 className="ca-header__title">Genre Explorer</h1>
          <p className="ca-header__subtitle">The Genre Explorer helps users discover and analyze category and genre trends across {(() => {
                const activePlatforms = getActivePlatforms();
                if (platformsLoading || activePlatforms.length === 0) {
                  return 'multiple VR platforms';
                }
                return activePlatforms.map(p => p.display_name).join(' and ');
              })()} stores in real time.
            It provides deep insights into how different categories perform, the top apps or games associated with them, and their download and revenue impact.
            Users can track category to stay updated on market shifts, competitor activity, and emerging content opportunities.

          </p>
        </div>
        <form className="ca-header__search" onSubmit={(e) => { e.preventDefault(); handleSearch(); }}>
          <div className="ca-search-input" ref={dropdownRef}>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => handleInputChange(e.target.value)}
              placeholder="Search here..."
              aria-label="Search categories"
              style={searchInput ? { paddingRight: '36px' } : {}}
            />
            {isSearchingSuggestions && (
              <div style={{
                position: 'absolute',
                right: searchInput ? "40px" : "12px",
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 2
              }}>
                <i className="fa fa-spinner fa-spin" style={{ color: '#F6255A', fontSize: '14px' }} />
              </div>
            )}
            {searchInput && !isSearchingSuggestions && (
              <button
                type="button"
                onClick={handleClearSearch}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#b0b0b5',
                  cursor: 'pointer',
                  padding: '4px',
                  fontSize: '18px',
                  lineHeight: '1'
                }}
                title="Clear search"
              >
                ✕
              </button>
            )}

            {/* Suggestions Dropdown */}
            {showDropdown && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  background: '#2A2B37',
                  border: '1px solid #3A3B47',
                  borderBottomLeftRadius: '6px',
                  borderBottomRightRadius: '6px',
                  marginTop: '1px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  zIndex: 2000,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
                  padding: '4px'
                }}
              >
                {suggestions.length > 0 ? (
                  suggestions.map((suggestion, index) => {
                    return (
                      <div
                        key={index}
                        onClick={() => handleSuggestionClick(suggestion)}
                        style={{
                          padding: '10px 16px',
                          cursor: 'pointer',
                          color: '#fff',
                          fontSize: '14px',
                          borderRadius: '4px',
                          backgroundColor: 'transparent',
                          transition: 'background 0.2s ease',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#3A3B47';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                        }}
                      >
                        {suggestion}
                      </div>
                    );
                  })
                ) : (
                  <div
                    style={{
                      padding: '12px 16px',
                      color: '#94a3b8',
                      fontSize: '13px',
                      textAlign: 'center',
                      fontStyle: 'italic'
                    }}
                  >
                    No matching genres found
                  </div>
                )}
              </div>
            )}
          </div>
          <div style={{ position: 'relative' }}>
            <button
              type="submit"
              onClick={handleSearch}
              className="ca-search-button"
              disabled={tableDataLoading}
            >
              {tableDataLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                </>
              ) : (
                'Search'
              )}
            </button>
          </div>
          <div style={{ marginLeft: 8, position: 'relative' }}>
            <button
              ref={filterButtonRef}
              type="button"
              className="filter-trigger"
              onClick={() => setShowFilters(true)}
              aria-expanded={showFilters}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ marginRight: 8 }} xmlns="http://www.w3.org/2000/svg">
                <path d="M10 18h4v-2h-4v2zM4 6h16v2H4V6zm3 6h10v-2H7v2z" fill="#fff" />
              </svg>
              Filter
              {appliedFilters && Object.keys(appliedFilters).length > 0 && (
                <span className="filter-badge">
                  {Object.values(appliedFilters).reduce((acc, v) => {
                    if (v == null) return acc
                    if (Array.isArray(v)) return acc + v.length
                    if (typeof v === 'string' || typeof v === 'number') return acc + (String(v).trim() ? 1 : 0)
                    return acc + 1
                  }, 0)}
                </span>
              )}
            </button>
          </div>
          {showFilters && (
            <FilterPanel
              open={showFilters}
              sections={filterSections.length > 0 ? filterSections : undefined}
              initialValues={appliedFilters}
              onClose={() => setShowFilters(false)}
              onApply={handleApplyFilters}
              // lock downloads & revenue sorts for Explorer (Tier-1) users only
              lockedOptions={isExplorerUser ? ["downloads", "revenue"] : []}
              onLockClick={() => setShowUpgradeModal(true)}
              anchorRef={filterButtonRef}
              gap={4}
            />
          )}
          <AppsTrackingPlanModal
            show={showUpgradeModal}
            handleClose={() => setShowUpgradeModal(false)}
            title=" Upgrade your plan to unlock this feature"
          />
        </form>

        {/* Applied filter badges for Category Explorer */}
        {Object.keys(appliedFilters || {}).length > 0 && (
          <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {Object.entries(appliedFilters).map(([key, value]) => {
              if (!value || (Array.isArray(value) && value.length === 0)) return null;
              let displayVal = Array.isArray(value) ? value.join(', ') : String(value);
              if (key === 'platform') {
                const ids = Array.isArray(value) ? value : [value];
                displayVal = ids
                  .map((id) => getActivePlatforms().find((p) => p.id === id)?.display_name || id)
                  .join(', ');
              }
              return (
                <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 8px', background: 'linear-gradient(90deg, rgba(255,154,184,0.12), rgba(246,37,90,0.12))', color: '#F6255A', borderRadius: 6, fontSize: 12, fontWeight: 500 }}>
                  <span style={{ textTransform: 'capitalize', marginRight: 4 }}>{key.replace(/_/g, ' ')}:</span>
                  <span style={{ opacity: 0.9, color: '#F6255A' }}>{displayVal}</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleRemoveFilter(key);
                    }}
                    aria-label={`Remove filter ${key}`}
                    style={{ marginLeft: 4, background: 'transparent', border: 'none', color: '#F6255A', cursor: 'pointer', fontSize: 12 }}
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </div>
        )}
        <style>{`
          .filter-trigger{
            background: transparent;
            border: 1px solid rgba(255,255,255,0.12);
            color: #fff;
            border-radius: 12px;
            padding: 8px 12px;
            display:inline-flex;
            align-items:center;
            gap:6px;
            height:44px;
            cursor:pointer;
            font-weight:600;
          }
          .filter-trigger svg{ opacity:0.95 }
        `}</style>
      </div>
    </header>
  )
}

export default Header